

import React, { useState, useEffect } from "react";
import {
  Box, TextField, Typography, Button, IconButton,
  Dialog, DialogContent, DialogActions,
  Grid, Paper, Alert, MenuItem, CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import axios from 'axios';
import {apiRequest} from '../../fetchSetup';


const PatientDetailsForm = ({ onComplete, initialData, userEmail, orderType }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [loadingUserData, setLoadingUserData] = useState(false);
  const [userFound, setUserFound] = useState(false);
  
  // Load initial data from props or sessionStorage
  const [phone, setPhone] = useState(() => initialData?.phone || "");
  const [doctorname, setDoctorName] = useState(() => initialData?.doctorname || "");
  const [name, setName] = useState(() => initialData?.name || "");
  const [dob, setDob] = useState(() => initialData?.dob || "");
  const [gender, setGender] = useState(() => initialData?.gender || "");
  const [prescription, setPrescription] = useState(() => initialData?.prescription || []);
  const [originalAge,setOriginalAge]=useState("")
  const [originalGender,setOriginalGender]=useState("")
  const [userId,setUserId] =useState("");
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Update form when initialData changes
  useEffect(() => {
    if (initialData) {
      setName(initialData.name || "");
      setDob(initialData.dob || "");
      setPhone(initialData.phone || "");
      setGender(initialData.gender || "");
      setDoctorName(initialData.doctorname || "");
      setPrescription(initialData.prescription || []);
    }
  }, [initialData]);

  //  Helper function to normalize gender value
  const normalizeGender = (genderValue) => {
    if (!genderValue) return "";
    
    const normalized = genderValue.toLowerCase().trim();
    
    // Map variations to standard values
    if (normalized === "male" || normalized === "m") return "Male";
    if (normalized === "female" || normalized === "f") return "Female";
    if (normalized === "other" || normalized === "others") return "other";
    
    // Return original if it matches exactly
    if (["Male", "Female", "other"].includes(genderValue)) return genderValue;
    
    // Default to original value
    return genderValue;
  };

  //  FIXED: Auto-fetch user details when phone number is complete
  const fetchUserByPhone = async (phoneNumber) => {
    let cleanedPhone = phoneNumber.trim();
    if (cleanedPhone.startsWith('+91')) cleanedPhone = cleanedPhone.substring(3);
    else if (cleanedPhone.startsWith('91') && cleanedPhone.length === 12) cleanedPhone = cleanedPhone.substring(2);
    cleanedPhone = cleanedPhone.replace(/[\s-]/g, '');
  
    if (cleanedPhone.length !== 10 || !/^[6-9]\d{9}$/.test(cleanedPhone)) return;
  
    setLoadingUserData(true);
    try {
      const apiUrl = process.env.REACT_APP_GET_USER_BY_PHONE_API;
      const response = await axios.get(`${apiUrl}/${cleanedPhone}`, {
        headers: { "Content-Type": "application/json" }
      });
  
      const data = response.data;
  
      if (data.success && data.user) {
        // ✅ Registered user - auto-fill
        setName(data.user.name || "");
        setGender(normalizeGender(data.user.gender));
        setDob(data.user.age || "");
        setOriginalAge(data.user.age)
        setOriginalGender(data.user.gender)
        setUserId(data.user.user_id)
        setUserFound(true);
        setDialogMessage(`Welcome back, ${data.user.name}! Your details have been loaded.`);
        setDialogOpen(true);
      } else {
        // Not found in response
        setUserFound(false);
        setName(""); setGender(""); setDob("");
        setDialogMessage("Phone number not registered. Please fill in the details manually.");
        setDialogOpen(true);
      }
    } catch (error) {
      setUserFound(false);
      setName(""); setGender(""); setDob("");
  
      if (error.response?.status === 404) {
        // ✅ Unregistered - show manual entry message
        setDialogMessage("Phone number not registered. Please fill in the details manually.");
      } else {
        // Network or server error
        setDialogMessage("Could not verify phone number. Please fill in the details manually.");
      }
      setDialogOpen(true);
    } finally {
      setLoadingUserData(false);
    }
  };

  //  NEW: Trigger fetch when phone is complete, clear data when phone changes
  useEffect(() => {
    if (phone.length === 10 && /^[6-9]\d{9}$/.test(phone)) {
      // Valid 10-digit phone - fetch user data
      fetchUserByPhone(phone);
    } else {
      // ✅ Phone changed or cleared - reset auto-filled data
      if (userFound) {
        console.log(" Phone changed/cleared - clearing auto-filled data");
        setName("");
        setGender("");
        setDob("");
        setUserFound(false);
        
        // Clear errors for cleared fields
        setErrors(prev => ({
          ...prev,
          name: "",
          gender: "",
          dob: ""
        }));
        
        // Reset touched state for cleared fields
        setTouched(prev => ({
          ...prev,
          name: false,
          gender: false,
          dob: false
        }));
      }
    }
  }, [phone]);

  const validateName = (value) => /^[A-Za-z\s]+$/.test(value);

  const validateField = (field, value) => {
    let error = "";

    switch (field) {
      case "name":
        if (!value.trim()) {
          error = "Name is required";
        } else if (value.trim().length < 2) {
          error = "Name must be at least 2 characters";
        } else if (!validateName(value)) {
          error = "Name should contain only alphabets";
        }
        break;

      case "dob":
        if (parseInt(value) < 1 || parseInt(value) > 200) {
          error = "Age must be between 1 and 200";
        }
        break;

      case "phone":
        if (!value) {
          error = "Phone number is required";
        } else if (value.length < 10) {
          error = "Phone must be 10 digits";
        } else if (value.length === 10 && !/^[6-9]\d{9}$/.test(value)) {
          error = "Enter a valid Indian phone number";
        }
        break;

      case "gender":
        if (!value) {
          error = "Please select a gender";
        }
        break;
    }

    return error;
  };
    const updateUserProfile = async (userId,age, gender) => {
      try {
        const token = sessionStorage.getItem("token");
        
        if (!token) {
          console.error("No authentication token found");
          return false;
        }
  
        const response = await apiRequest(process.env.REACT_APP_UPDATE_USER_PROFILE_API, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            user_id:userId,
            age: parseInt(age),
            gender: gender
          })
        });
  
        const data = await response.json();
  
        if (data.success) {
          // Update session storage with new user data
          const updatedUser = data.user;
          sessionStorage.setItem("user", JSON.stringify(updatedUser));
          
          console.log(" User profile updated successfully");
          return true;
        } else {
          console.error("Failed to update profile:", data.error);
          return false;
        }
      } catch (error) {
        console.error("Error updating user profile:", error);
        return false;
      }
    };

  const handleContinue = () => {
    const fieldsToValidate = {
      phone,
      name,
      dob,
      gender,
    };

    const newErrors = {};
    Object.keys(fieldsToValidate).forEach((field) => {
      const error = validateField(field, fieldsToValidate[field]);
      if (error) {
        newErrors[field] = error;
      }
    });

    setErrors(newErrors);
    setTouched({
      phone: true,
      name: true,
      dob: true,
      gender: true,
    });

    if (Object.keys(newErrors).length > 0) {
      setDialogMessage("Please fill all required fields correctly");
      setDialogOpen(true);
      return;
    }

    const patientDetails = {
      patientType: "new",
      phone,
      name,
      dob,
      gender,
      doctorname,
      prescription,
      userEmail,
      orderType,
    };
    const ageChanged = dob !== originalAge;
    const genderChanged = gender !== originalGender;
      
      if (ageChanged || genderChanged) {
        // Update the profile in backend
        const updateSuccess = updateUserProfile(userId, dob, gender);
        
        if (!updateSuccess) {
          setDialogMessage("Failed to update profile. Please try again.");
          setDialogOpen(true);
          return;
        }
      }

    console.log("📋 Patient details being passed:", patientDetails);
    onComplete(patientDetails);
  };

  const fieldStyle = {
    '& .MuiInputBase-root': {
      borderRadius: 2,
    },
  };

  return (
    <>
    {/* <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
      <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', mb: 3 }}>
        Patient Information
      </Typography>

      <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
        Enter phone number to auto-fill details (if registered)
      </Alert> */}
      <Box sx={{ width: '100%', maxWidth: '700px', mx: 'auto', px: { xs: 2, sm: 3 },  py: { xs: 2, sm: 3 }, }} >
<Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, border: '1px solid #e2e8f0',width: '100%', }}>
    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', mb: 3,fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
    Patient Information
    </Typography>

    <Alert severity="info" sx={{ mb: 3, borderRadius: 2,fontSize: { xs: '0.875rem', sm: '1rem' }}}>
      Enter phone number to auto-fill details (if registered)
    </Alert>

      <Grid container spacing={2.5} direction="column">
        {/* ✅ PHONE FIRST */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Phone Number"
            placeholder="Enter 10-digit phone number"
            value={phone}
            onChange={(e) => {
              const value = e.target.value;
              if (/^\d{0,10}$/.test(value)) {
                setPhone(value);
                if (touched.phone) {
                  const error = validateField("phone", value);
                  setErrors((prev) => ({ ...prev, phone: error }));
                }
              }
            }}
            onBlur={() => {
              setTouched((prev) => ({ ...prev, phone: true }));
              const error = validateField("phone", phone);
              setErrors((prev) => ({ ...prev, phone: error }));
            }}
            inputProps={{ maxLength: 10 }}
            type="tel"
            error={touched.phone && !!errors.phone}
            helperText={touched.phone && errors.phone}
            InputProps={{
              endAdornment: loadingUserData ? (
                <CircularProgress size={20} />
              ) : userFound ? (
                <Typography variant="caption" color="success.main">✓ Found</Typography>
              ) : null
            }}
            sx={{
              ...fieldStyle, 
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: '#FAFAFA',
              }
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Full Name"
            placeholder="Enter full name"
            value={name}
            onChange={(e) => {
              const value = e.target.value;
              if (/^[A-Za-z\s]*$/.test(value)) {
                setName(value);
                if (touched.name) {
                  const error = validateField("name", value);
                  setErrors((prev) => ({ ...prev, name: error }));
                }
              }
            }}
            onBlur={() => {
              setTouched((prev) => ({ ...prev, name: true }));
              const error = validateField("name", name);
              setErrors((prev) => ({ ...prev, name: error }));
            }}
            error={touched.name && !!errors.name}
            helperText={touched.name && errors.name}
            disabled={loadingUserData}
            sx={{
              ...fieldStyle, 
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: '#FAFAFA',
              }
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Age"
            type="number"
            placeholder="Enter age"
            value={dob}
            inputProps={{ min: 1, max: 200 }}
            onChange={(e) => {
              const value = e.target.value;
              if (value === "" || (/^\d+$/.test(value) && value.length <= 3 && parseInt(value) > 0)) {
                setDob(value);
                if (touched.dob) {
                  const error = validateField("dob", value);
                }
              }
            }}
            onBlur={() => {
              setTouched((prev) => ({ ...prev, dob: true }));
              const error = validateField("dob", dob);
            }}
            error={touched.dob && !!errors.dob}
            helperText={touched.dob && errors.dob}
            disabled={loadingUserData}
            sx={{
              ...fieldStyle, 
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: '#FAFAFA',
              }
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            select
            fullWidth
            value={gender}
            onChange={(e) => {
              setGender(e.target.value);
              setTouched((prev) => ({ ...prev, gender: true }));
              setErrors((prev) => ({ ...prev, gender: "" }));
            }}
            error={touched.gender && !!errors.gender}
            helperText={touched.gender && errors.gender}
            disabled={loadingUserData}
            SelectProps={{
              displayEmpty: true,
              renderValue: (selected) => {
                if (!selected) {
                  return <span style={{ color: '#9CA3AF' }}>Select Gender</span>;
                }
                return selected;
              }
            }}
            sx={{
              ...fieldStyle, 
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: '#FAFAFA',
              }
            }}
          >
            <MenuItem value="" disabled>
              Select Gender
            </MenuItem>
            <MenuItem value="Male">Male</MenuItem>
            <MenuItem value="Female">Female</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </TextField>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Doctor Name (Optional)"
            placeholder="Enter Doctor Name"
            value={doctorname}
            onChange={(e) => {
              const value = e.target.value;
              if (/^[A-Za-z\s]*$/.test(value)) {
                setDoctorName(value);
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: '#FAFAFA',
              }
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <Button
            component="label"
            fullWidth
            variant="outlined"
            startIcon={<AttachFileIcon />}
            sx={{
              py: 1.5,
              borderRadius: 2,
              borderColor: '#E0E0E0',
              color: '#666',
              textTransform: 'none',
              fontSize: 14,
              fontWeight: 500,
              bgcolor: '#FAFAFA',
              '&:hover': {
                bgcolor: '#F5F5F5',
                borderColor: '#006D77'
              }
            }}
          >
            Medical Documents (Optional)
            <input
              type="file"
              hidden
              multiple
              accept="image/*,.pdf"
              onChange={(e) => setPrescription(Array.from(e.target.files))}
            />
          </Button>
        </Grid>
      </Grid>

      {prescription.length > 0 && (
        <Box sx={{ mt: 3, p: 2, bgcolor: '#F9F9F9', borderRadius: 2 }}>
          <Typography variant="body2" fontWeight={600} mb={1.5} color="#333">
            Uploaded Files ({prescription.length})
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {prescription.map((file, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 1.5,
                  bgcolor: '#fff',
                  borderRadius: 1.5,
                  border: '1px solid #E0E0E0'
                }}
              >
                <Typography fontSize={13} color="#555">
                  📄 {file.name}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() =>
                    setPrescription((prev) => prev.filter((_, i) => i !== index))
                  }
                  sx={{ 
                    color: '#999',
                    '&:hover': {
                      color: '#EF4444',
                      bgcolor: '#FEE2E2'
                    }
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      <Button
        fullWidth
        variant="contained"
        sx={{
          mt: 4,
          py: 1.8,
          borderRadius: 2.5,
          bgcolor: "#006D77",
          fontSize: 15,
          fontWeight: 600,
          textTransform: 'none',
          boxShadow: '0 4px 12px rgba(0, 109, 119, 0.3)',
          "&:hover": { 
            bgcolor: "#004F55",
            boxShadow: '0 6px 16px rgba(0, 109, 119, 0.4)',
          },
        }}
        onClick={handleContinue}
        disabled={loadingUserData}
      >
        {loadingUserData ? "Loading..." : "Continue to Medicine Selection"}
      </Button>

      <Dialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)}
        PaperProps={{
          sx: { borderRadius: 3, minWidth: 300 }
        }}
      >
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ fontSize: 15 }}>{dialogMessage}</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setDialogOpen(false)}
            variant="contained"
            fullWidth
            sx={{
              bgcolor: '#006D77',
              textTransform: 'none',
              '&:hover': { bgcolor: '#004F55' }
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
    </Box>
    </>
  );
};

export default PatientDetailsForm;

